import type { ElementDefinition } from 'cytoscape';

import { getIconOrDefault } from '@/sections/insights/queryBuilder/utils/graphIcons';
import type { GraphQueryResponse, RelationshipNode, TreeNode } from '@/types';

/**
 * Extract entity type from key field
 * Keys are formatted as "#type#id", e.g., "#asset#123" or "#addomain#456"
 * @param key - The entity key
 * @returns The entity type (e.g., 'asset', 'addomain') or undefined
 */
export function extractTypeFromKey(key: string): string | undefined {
  if (!key || !key.startsWith('#')) return undefined;
  const parts = key.split('#');
  // parts[0] is empty, parts[1] is the type
  return parts[1] || undefined;
}

/**
 * Maps entity type from key to drawer type for routing
 *
 * @param key - The entity key (e.g., "#asset#123", "#risk#456")
 * @returns Drawer type ('asset' | 'vulnerability'), or undefined if no drawer available
 *
 * @example
 * mapEntityTypeToDrawer("#asset#123") → 'asset'
 * mapEntityTypeToDrawer("#risk#456") → 'vulnerability'
 * mapEntityTypeToDrawer("#seed#789") → undefined
 */
export function mapEntityTypeToDrawer(
  key: string | undefined
): 'asset' | 'vulnerability' | undefined {
  if (!key) return undefined;

  const entityType = extractTypeFromKey(key);
  if (!entityType) return undefined;

  // Map entity types to drawer types
  switch (entityType) {
    case 'asset':
    case 'adobject':
    case 'aduser':
    case 'adcomputer':
    case 'adgroup':
    case 'addomain':
    case 'awsresource':
    case 'port':
      return 'asset';

    case 'risk':
    case 'vulnerability':
      return 'vulnerability';

    default:
      // No drawer available for this entity type (seeds, technologies, etc.)
      return undefined;
  }
}

/**
 * Transform graph query response (TreeNode[]) to Cytoscape elements with icons
 * Simplified transformation - produces ElementDefinition[] compatible with Cytoscape
 *
 * @param response - Array of TreeNode objects from graph query
 * @param theme - Current theme ('light' or 'dark') for icon generation
 * @returns Array of Cytoscape-compatible element definitions
 */
export function transformGraphResponseToCyto(
  response: TreeNode[] | null | undefined,
  theme: 'light' | 'dark'
): ElementDefinition[] {
  if (!response || !Array.isArray(response)) {
    return [];
  }

  const elements: ElementDefinition[] = [];
  const seenNodes = new Set<string>();

  /**
   * Recursively process node and its relationships
   */
  function processNode(treeNode: TreeNode): void {
    const { result, relationships } = treeNode.node;

    // Extract node data from result (could be asset, vuln, etc.)
    const nodeData = extractNodeData(result);
    if (!nodeData) return;

    const { id, label, type, iconType, source } = nodeData;

    // Add node if not seen
    if (!seenNodes.has(id)) {
      seenNodes.add(id);

      // Get icon for this entity type
      const { icon } = getIconOrDefault(iconType, theme);

      elements.push({
        data: {
          id,
          key: id, // Add key field for drawer routing
          label,
          type,
          iconType,
          icon, // Include icon data URL directly
          source,
        },
      });
    }

    // Process relationships recursively
    if (relationships && Array.isArray(relationships)) {
      relationships.forEach(rel => processRelationship(id, rel));
    }
  }

  /**
   * Process a single relationship and its target node
   * Adds target node (if not seen) and creates edge
   * Recursively processes nested relationships
   */
  function processRelationship(sourceId: string, rel: RelationshipNode): void {
    if (!rel.target) return;

    const targetData = extractNodeData(rel.target.result);
    if (!targetData) return;

    // Add target node
    if (!seenNodes.has(targetData.id)) {
      seenNodes.add(targetData.id);

      // Get icon for target entity type
      const { icon } = getIconOrDefault(targetData.iconType, theme);

      elements.push({
        data: {
          id: targetData.id,
          key: targetData.id, // Add key field for drawer routing
          label: targetData.label,
          type: targetData.type,
          iconType: targetData.iconType,
          icon, // Include icon data URL directly
        },
      });
    }

    // Add edge
    const edgeId = `${sourceId}-${rel.label || 'related'}-${targetData.id}`;
    elements.push({
      data: {
        id: edgeId,
        source: sourceId,
        target: targetData.id,
        label: rel.label,
      },
    });

    // Recurse into target's relationships
    if (rel.target.relationships) {
      processNode({
        node: {
          result: rel.target.result,
          relationships: rel.target.relationships,
        },
      });
    }
  }

  /**
   * Extract node ID, label, and type information from result object
   * Checks all possible entity types (assets, vulnerabilities, etc.)
   */
  function extractNodeData(result: GraphQueryResponse): {
    id: string;
    label: string;
    type?: string;
    iconType?: string;
    source?: string;
  } | null {
    // Try assets
    if (result.assets?.[0]?.key) {
      const key = result.assets[0].key;
      return {
        id: key,
        label:
          result.assets[0].identifier || result.assets[0].name || 'Unknown',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
      };
    }

    // Try vulnerabilities
    if (result.vulnerabilities?.[0]?.key) {
      const key = result.vulnerabilities[0].key;
      const vuln = result.vulnerabilities[0] as unknown as {
        key: string;
        name?: string;
        id?: string;
      };
      return {
        id: key,
        label: vuln.name || vuln.id || 'Unknown',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
      };
    }

    // Try risks
    if (result.risks?.[0]?.key) {
      const key = result.risks[0].key;
      return {
        id: key,
        label: result.risks[0].name || 'Unknown',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
      };
    }

    // Try AD objects
    if (result.adobjects?.[0]?.key) {
      const key = result.adobjects[0].key;
      const adObject = result.adobjects[0];
      return {
        id: key,
        label: adObject.name || adObject.identifier || 'Unknown',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
      };
    }

    // Try AWS resources
    if (result.awsresources?.[0]?.key) {
      const key = result.awsresources[0].key;
      const resource = result.awsresources[0];
      return {
        id: key,
        label: resource.displayName || resource.name || 'Unknown',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
      };
    }

    // Try seeds (seeds is Record<string, unknown>, needs type assertion)
    if (result.seeds) {
      const seedArray = result.seeds as unknown as Array<{
        key?: string;
        identifier?: string;
      }>;
      if (seedArray[0]?.key) {
        const key = seedArray[0].key;
        return {
          id: key,
          label: seedArray[0].identifier || 'Unknown',
          type: extractTypeFromKey(key),
          iconType: extractTypeFromKey(key),
        };
      }
    }

    // Try technologies (type doesn't include identifier, but it exists at runtime)
    if (result.technologies?.[0]?.key) {
      const key = result.technologies[0].key;
      const tech = result.technologies[0] as unknown as {
        key: string;
        identifier?: string;
        product?: string;
      };
      return {
        id: key,
        label: tech.identifier || tech.product || 'Unknown',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
      };
    }

    // Try ports
    if (result.ports?.[0]?.key) {
      const port = result.ports[0];
      const key = port.key;
      const source = port.source;

      // Construct label: "80/tcp" or use service name if available
      const portLabel = port.service
        ? `${port.port}/${port.protocol} (${port.service})`
        : `${port.port}/${port.protocol}`;

      return {
        id: key,
        label: portLabel || 'Unknown Port',
        type: extractTypeFromKey(key),
        iconType: extractTypeFromKey(key),
        source: source,
      };
    }

    return null;
  }

  // Process all root nodes
  response.forEach(treeNode => {
    if (treeNode?.node) {
      processNode(treeNode);
    }
  });

  return elements;
}
